(function () {
  const SECTION_CLASS = "feature-details-section";
  const DESKTOP_BREAKPOINT = 990;
  const PANEL_MS = 400;
  const PANEL_EASING = "ease-in-out";
  // The copy is laid out at the width the card SETTLES at, so while the card is
  // still travelling it runs past the edge and is cut by it. It is faded in over
  // the last stretch of the travel instead, and out over the first — so it is only
  // ever read inside a box that already fits it.
  const PANEL_FADE_MS = 140;
  const PANEL_OPEN_TRANSITION = `height ${PANEL_MS}ms ${PANEL_EASING}, opacity ${PANEL_FADE_MS}ms ${PANEL_EASING} ${
    PANEL_MS - PANEL_FADE_MS
  }ms`;
  const PANEL_CLOSE_TRANSITION = `height ${PANEL_MS}ms ${PANEL_EASING}, opacity ${PANEL_FADE_MS}ms ${PANEL_EASING}`;
  // The card carries its own height rather than letting the panel push it: a closing
  // card's height stalls on the icon's 2.4rem floor while the opening one is still
  // growing, so the column's total would shrink and then grow again over the last
  // stretch — and a centred column turns that into every card stepping back a pixel.
  const ITEM_TRANSITION = `width ${PANEL_MS}ms ${PANEL_EASING}, height ${PANEL_MS}ms ${PANEL_EASING}`;
  const REVEAL_CLASS = "feature-details__media-item--revealing";
  const ROLL_CLASS = "is-rolling";
  const PANEL_INNER = ".feature-details__item-description-inner";
  // How far a drag must travel before it is read as sideways rather than the page's
  // own vertical one, how long the strip takes to settle on release, and how much of
  // a pull past either end actually lands.
  const AXIS_LOCK = 6;
  const SNAP_MS = 300;
  const EDGE_GIVE = 0.3;

  class FeatureDetails {
    constructor(el) {
      this.el = el;
      this.items = Array.from(
        el.querySelectorAll("[data-feature-details-item]")
      );
      this.medias = Array.from(
        el.querySelectorAll("[data-feature-details-media]")
      );
      this.total = this.items.length;
      if (!this.total) return;

      this.controller = new AbortController();
      this.swiper = null;
      this.revealingLayer = null;
      this.pointer = null;
      this.reconcileTimer = null;
      this.reconciled = new Set();
      this.desktopQuery = window.matchMedia(
        `(min-width: ${DESKTOP_BREAKPOINT}px)`
      );

      const marked = this.items.findIndex((item) =>
        item.classList.contains("active")
      );
      this.current = marked === -1 ? 0 : marked;

      this.items.forEach((item, index) => this._bindItem(item, index));
      this._bindPointer();
      this._bindNavigation();
      this._bindMediaGesture();

      this.desktopQuery.addEventListener("change", () => this._syncMode(), {
        signal: this.controller.signal,
      });

      this._applyState();
      this._syncMode();
    }

    activate(index) {
      if (index < 0 || index >= this.total || index === this.current) return;

      const previous = this.current;
      // The pair is captured before the class moves: `active` alone collapses the
      // outgoing card in one frame, which read as it closing ahead of the new one.
      const swap = this.desktopQuery.matches
        ? this._captureSwap(previous, index)
        : null;

      this.current = index;

      this._applyState();
      this._revealMedia(index);

      if (swap) this._runSwap(swap);

      if (this.swiper && this.swiper.activeIndex !== index) {
        this.swiper.slideTo(index);
      }

      // Every travel runs for PANEL_MS, so this lands once the column has settled —
      // and restarts from the last one if another card is taken in the meantime.
      window.clearTimeout(this.reconcileTimer);
      this.reconcileTimer = window.setTimeout(
        () => this._reconcileHover(),
        PANEL_MS
      );
    }

    destroy() {
      if (!this.controller) return;
      window.clearTimeout(this.reconcileTimer);
      this._releaseReveal();
      this._destroySlider();
      this.items.forEach((item) => this._resetItem(item));
      this.controller.abort();
    }

    /* ── State ── */

    _applyState() {
      this.items.forEach((item, index) => {
        const isActive = index === this.current;
        item.classList.toggle("active", isActive);

        const toggle = item.querySelector("[data-feature-details-toggle]");
        if (toggle) {
          toggle.setAttribute(
            "aria-expanded",
            String(this.desktopQuery.matches ? isActive : true)
          );
        }
      });

      this._updateNavigation();
    }

    _panelOf(target) {
      const item = typeof target === "number" ? this.items[target] : target;
      if (!item) return null;
      return item.querySelector("[data-feature-details-panel]");
    }

    /* ── Media reveal ── */

    _revealMedia(index) {
      this.medias.forEach((layer, i) => {
        const isActive = i === index;
        layer.classList.toggle("active", isActive);
        layer.setAttribute("aria-hidden", isActive ? "false" : "true");
      });

      this._releaseReveal();

      const next = this.medias[index];
      if (!next) return;

      // Re-adding after a reflow restarts the reveal on every switch; the layer the
      // page opened on never carries it, so the first paint stays static.
      next.classList.remove(REVEAL_CLASS);
      void next.offsetWidth;
      next.classList.add(REVEAL_CLASS);
      this.revealingLayer = next;

      next.addEventListener("animationend", () => this._releaseReveal(), {
        once: true,
        signal: this.controller.signal,
      });
    }

    _releaseReveal() {
      if (!this.revealingLayer) return;
      this.revealingLayer.classList.remove(REVEAL_CLASS);
      this.revealingLayer = null;
    }

    /* ── Panels ── */

    // Reads both cards as they stand, then hands them back to the stylesheet so the
    // boxes they settle into can be read straight after the class change.
    _captureSwap(previous, next) {
      const closing = this._measureItem(this.items[previous]);
      const opening = this._measureItem(this.items[next]);

      this._resetItem(closing.item);
      this._resetItem(opening.item);

      return { closing, opening };
    }

    _measureItem(item) {
      const panel = this._panelOf(item);
      const inner = panel ? panel.querySelector(PANEL_INNER) : null;

      return {
        item,
        panel,
        inner,
        width: item.getBoundingClientRect().width,
        itemHeight: item.getBoundingClientRect().height,
        height: panel ? panel.getBoundingClientRect().height : 0,
        innerWidth: inner ? inner.getBoundingClientRect().width : 0,
        opacity: panel ? window.getComputedStyle(panel).opacity : "1",
      };
    }

    _runSwap(swap) {
      const moves = [
        { from: swap.closing, to: this._measureItem(swap.closing.item) },
        { from: swap.opening, to: this._measureItem(swap.opening.item) },
      ];

      moves.forEach((move) => this._primeMove(move));

      // One flush for the pair: both start boxes land in the same style recalc, so the
      // close and the open then run over the same frames instead of in turn.
      void this.el.offsetHeight;

      moves.forEach((move) => this._playMove(move));
    }

    _primeMove(move) {
      const { item, panel, inner } = move.from;

      item.style.transition = "none";
      item.style.width = `${move.from.width}px`;
      item.style.height = `${move.from.itemHeight}px`;

      if (!panel) return;

      // The copy keeps the width it has while the card is open, so it never re-wraps
      // mid-travel — the panel clips it instead.
      const openWidth = Math.max(move.from.innerWidth, move.to.innerWidth);
      if (inner && openWidth) inner.style.width = `${openWidth}px`;

      // Held in flow and visible for the whole travel: the stylesheet takes it out at
      // once, and the card has to keep following its height.
      panel.style.transition = "none";
      panel.style.position = "static";
      panel.style.visibility = "visible";
      panel.style.height = `${move.from.height}px`;
      panel.style.opacity = move.from.opacity;
    }

    _playMove(move) {
      const { item, panel } = move.from;
      let travelling = false;

      const widthTravels =
        Math.round(move.to.width) !== Math.round(move.from.width);
      const heightTravels =
        Math.round(move.to.itemHeight) !== Math.round(move.from.itemHeight);

      if (widthTravels || heightTravels) {
        item.style.transition = ITEM_TRANSITION;
        item.style.width = `${move.to.width}px`;
        item.style.height = `${move.to.itemHeight}px`;
        this._watch(item, heightTravels ? "height" : "width", item);
        travelling = true;
      }

      if (
        panel &&
        Math.round(move.to.height) !== Math.round(move.from.height)
      ) {
        const opening =
          parseFloat(move.to.opacity) > parseFloat(move.from.opacity);
        panel.style.transition = opening
          ? PANEL_OPEN_TRANSITION
          : PANEL_CLOSE_TRANSITION;
        panel.style.height = `${move.to.height}px`;
        panel.style.opacity = move.to.opacity;
        this._watch(panel, "height", item);
        travelling = true;
      }

      if (!travelling) this._resetItem(item);
    }

    _watch(el, property, item) {
      this._unwatch(el);

      const onEnd = (event) => {
        if (event.propertyName !== property) return;
        this._resetItem(item);
      };

      el._featureDetailsEnd = onEnd;
      el.addEventListener("transitionend", onEnd, {
        signal: this.controller.signal,
      });
    }

    _unwatch(el) {
      if (!el || !el._featureDetailsEnd) return;
      el.removeEventListener("transitionend", el._featureDetailsEnd);
      el._featureDetailsEnd = null;
    }

    // Every measured value is only right for the copy and the width as they stand now,
    // so a settled card hands its whole box back to the stylesheet.
    _resetItem(item) {
      if (!item) return;

      const panel = this._panelOf(item);
      const inner = panel ? panel.querySelector(PANEL_INNER) : null;

      this._unwatch(item);
      this._unwatch(panel);

      item.style.removeProperty("transition");
      item.style.removeProperty("width");
      item.style.removeProperty("height");
      if (!panel) return;

      if (inner) inner.style.removeProperty("width");
      panel.style.removeProperty("transition");
      panel.style.removeProperty("position");
      panel.style.removeProperty("visibility");
      panel.style.removeProperty("height");
      panel.style.removeProperty("opacity");
    }

    /* ── Controls ── */

    _bindItem(item, index) {
      const toggle = item.querySelector("[data-feature-details-toggle]");
      if (!toggle) return;

      const onHover = (event) => {
        if (!this.desktopQuery.matches) return;
        // A card arriving under a pointer that has not moved is the column travelling,
        // not the reader choosing — acting on it hands the card to a neighbour halfway
        // through its own travel and the list lurches back.
        if (!this._pointerMoved(event)) return;
        // Read before activate(): a card that is already the open one is the only
        // one whose heading rolls — arriving at a closed card opens it instead, and
        // the heading sits still for that travel.
        if (index === this.current) item.classList.add(ROLL_CLASS);
        this.activate(index);
      };

      item.addEventListener("mouseenter", onHover, {
        signal: this.controller.signal,
      });
      item.addEventListener(
        "mouseleave",
        () => item.classList.remove(ROLL_CLASS),
        { signal: this.controller.signal }
      );
      toggle.addEventListener("focusin", onHover, {
        signal: this.controller.signal,
      });
      toggle.addEventListener(
        "focusout",
        () => item.classList.remove(ROLL_CLASS),
        { signal: this.controller.signal }
      );

      toggle.addEventListener("click", () => this.activate(index), {
        signal: this.controller.signal,
      });

      toggle.addEventListener(
        "keydown",
        (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          this.activate(index);
        },
        { signal: this.controller.signal }
      );
    }

    // Opening a card re-centres the whole column, so every card travels — and a card
    // that slides beneath a still pointer raises `mouseenter` just as a real hover
    // does. The last place the reader actually moved to is kept here to tell the two
    // apart: a genuine arrival always carries a position the pointer has not been at.
    _bindPointer() {
      this.el.addEventListener(
        "mousemove",
        (event) => {
          this.pointer = { x: event.clientX, y: event.clientY };
          // The reader aiming somewhere new is a fresh decision, so the cards the
          // hand-over already chased stop counting against it.
          this.reconciled.clear();
        },
        { signal: this.controller.signal }
      );
    }

    _pointerMoved(event) {
      if (!event || event.type !== "mouseenter" || !this.pointer) return true;
      return (
        event.clientX !== this.pointer.x || event.clientY !== this.pointer.y
      );
    }

    // The arrival that _pointerMoved turned down still happened: the card really is
    // under the pointer, it just could not be taken mid-travel without the list
    // lurching. Once everything has settled the hover is read back off the DOM —
    // whatever sits under the pointer now is what the reader is pointing at.
    _reconcileHover() {
      if (!this.desktopQuery.matches) return;

      const index = this.items.findIndex((item) => item.matches(":hover"));
      if (index === -1 || index === this.current) return;
      // Settling one card can slide the next under a pointer that never moved. That
      // is worth following once, but never back to a card this same rest already
      // went through — two cards trading places would otherwise never come to rest.
      if (this.reconciled.has(index)) return;

      this.reconciled.add(index);
      this.activate(index);
    }

    _bindNavigation() {
      const nav = this.el.querySelector("[data-feature-details-nav]");
      if (!nav) return;

      const list = this.el.querySelector(".feature-details__items");
      this.prevButton = nav.querySelector(".slider-nav-button--prev");
      this.nextButton = nav.querySelector(".slider-nav-button--next");

      const pairs = [
        [this.prevButton, nav.dataset.prevLabel, -1],
        [this.nextButton, nav.dataset.nextLabel, 1],
      ];

      pairs.forEach(([button, label, step]) => {
        if (!button) return;

        button.setAttribute("type", "button");
        if (label) button.setAttribute("aria-label", label);
        // The shared control stays invisible until it names what it drives.
        if (list && list.id) button.setAttribute("aria-controls", list.id);

        button.addEventListener(
          "click",
          () => {
            if (button.getAttribute("aria-disabled") === "true") return;
            this.activate(this.current + step);
          },
          { signal: this.controller.signal }
        );
      });

      this._updateNavigation();
    }

    // The frames are stacked and cross-fade, so they cannot follow a finger
    // themselves. The drag across them is handed to the tab strip's own slider
    // instead: the strip travels under the finger exactly as it does when dragged
    // directly, snaps on release, and the frame changes with the slide it settles on.
    _bindMediaGesture() {
      const media = this.el.querySelector(".feature-details__media");
      if (!media || this.total < 2) return;

      let startX = 0;
      let startY = 0;
      let startTranslate = 0;
      let pointerId = null;
      // Unset until the travel is long enough to tell a sideways drag from the
      // page's own vertical one; the page keeps every gesture that is not ours.
      let axis = null;

      // Below the breakpoint the strip is a slider; above it the column is driven
      // by hover and there is nothing for the image to pull.
      const slider = () => (this.desktopQuery.matches ? null : this.swiper);

      const release = () => {
        const swiper = slider();
        pointerId = null;
        if (!swiper || axis !== "x") {
          axis = null;
          return;
        }
        axis = null;

        const position = swiper.rtlTranslate
          ? swiper.getTranslate()
          : -swiper.getTranslate();

        let index = 0;
        let nearest = Infinity;
        swiper.slidesGrid.forEach((slidePosition, i) => {
          const distance = Math.abs(slidePosition - position);
          if (distance < nearest) {
            nearest = distance;
            index = i;
          }
        });

        swiper.slideTo(index, SNAP_MS);
      };

      media.addEventListener(
        "pointerdown",
        (event) => {
          if (!slider()) return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startY = event.clientY;
          axis = null;
          // The finger leaving the frame mid-drag must not end it — the strip is
          // still being pulled.
          if (media.setPointerCapture) media.setPointerCapture(event.pointerId);
        },
        { signal: this.controller.signal }
      );

      media.addEventListener(
        "pointermove",
        (event) => {
          if (pointerId === null || event.pointerId !== pointerId) return;
          const swiper = slider();
          if (!swiper) return;

          const dx = event.clientX - startX;
          const dy = event.clientY - startY;

          if (axis === null) {
            if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
            axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
            if (axis !== "x") return;
            startTranslate = swiper.getTranslate();
            swiper.setTransition(0);
          }

          if (axis !== "x") return;
          event.preventDefault();

          // Past either end the strip gives, rather than stopping dead or running
          // off — the same slack Swiper's own drag has there.
          const loose = swiper.minTranslate();
          const taut = swiper.maxTranslate();
          let translate = startTranslate + dx;
          if (translate > loose) translate = loose + (translate - loose) * EDGE_GIVE;
          if (translate < taut) translate = taut + (translate - taut) * EDGE_GIVE;

          swiper.setTranslate(translate);
        },
        { passive: false, signal: this.controller.signal }
      );

      media.addEventListener("pointerup", release, {
        signal: this.controller.signal,
      });
      media.addEventListener("pointercancel", release, {
        signal: this.controller.signal,
      });

      // A mouse press on a photo is a native image drag unless it is turned down.
      media.addEventListener("dragstart", (event) => event.preventDefault(), {
        signal: this.controller.signal,
      });
    }

    _updateNavigation() {
      const states = [
        [this.prevButton, this.current <= 0, this.nextButton],
        [this.nextButton, this.current >= this.total - 1, this.prevButton],
      ];

      states.forEach(([button, isDisabled, counterpart]) => {
        if (!button) return;
        button.classList.toggle("swiper-button-disabled", isDisabled);
        button.setAttribute("aria-disabled", String(isDisabled));
        // Safari's default Tab order skips native buttons and stops only on an
        // explicit tabindex, so an enabled button carries one — as Swiper's own do.
        button.setAttribute("tabindex", isDisabled ? "-1" : "0");

        // A button that just reached the end would otherwise keep focus it can no
        // longer act on.
        if (isDisabled && document.activeElement === button && counterpart) {
          counterpart.focus();
        }
      });
    }

    /* ── Mobile slider ── */

    _syncMode() {
      // The cards go back to the stylesheet first: a measured box from the other mode
      // would be the one Swiper sizes its slides from.
      this.items.forEach((item) => this._resetItem(item));

      if (this.desktopQuery.matches) {
        this._destroySlider();
      } else {
        this._initSlider();
      }

      this._applyState();
    }

    _initSlider() {
      if (this.swiper || this.total < 2) return;
      if (typeof window.Swiper === "undefined") return;

      const root = this.el.querySelector(".feature-details__items");
      const track = this.el.querySelector(".feature-details__items-track");
      if (!root || !track) return;

      root.classList.add("swiper");
      track.classList.add("swiper-wrapper");
      this.items.forEach((item) => item.classList.add("swiper-slide"));

      this.swiper = new window.Swiper(root, {
        slidesPerView: "auto",
        spaceBetween: this._slideGap(),
        speed: 400,
        watchSlidesProgress: true,
        allowTouchMove: true,
        mousewheel: {
          forceToAxis: true,
          eventsTarget: this._wheelTarget(),
        },
        a11y: {
          scrollOnFocus: false,
        },
      });

      this.swiper.on("slideChange", () =>
        this.activate(this.swiper.activeIndex)
      );
      this.swiper.slideTo(this.current, 0);
    }

    // The whole card reads the sideways wheel, not just the strip, so the frames
    // answer the same two fingers the tabs do — one reader, so both travel with
    // exactly the same feel. Swiper resolves this by selector, so the card is
    // named after the strip's own id, which already carries the section's.
    _wheelTarget() {
      const card = this.el.querySelector(".feature-details__card");
      const root = this.el.querySelector(".feature-details__items");
      if (!card || !root || !root.id) return "container";

      if (!card.id) card.id = `${root.id}-card`;
      const escape = window.CSS && window.CSS.escape;
      return `#${escape ? escape(card.id) : card.id}`;
    }

    _destroySlider() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      const root = this.el.querySelector(".feature-details__items");
      const track = this.el.querySelector(".feature-details__items-track");
      if (root) root.classList.remove("swiper");
      if (track) track.classList.remove("swiper-wrapper");
      this.items.forEach((item) => item.classList.remove("swiper-slide"));
    }

    // The track's CSS gap is dropped once Swiper owns the spacing, so the slider
    // reads the same value back instead of holding a second copy of it.
    _slideGap() {
      const raw = window
        .getComputedStyle(this.el)
        .getPropertyValue("--feature-details-gap")
        .trim();
      const value = parseFloat(raw);
      if (Number.isNaN(value)) return 12;

      if (!raw.endsWith("rem")) return value;

      const rootSize = parseFloat(
        window.getComputedStyle(document.documentElement).fontSize
      );
      return value * (Number.isNaN(rootSize) ? 10 : rootSize);
    }
  }

  function destroySection(section) {
    if (!section || typeof section.querySelectorAll !== "function") return;

    section.querySelectorAll(".feature-details").forEach((el) => {
      if (!el._featureDetailsInstance) return;
      el._featureDetailsInstance.destroy();
      el._featureDetailsInstance = null;
    });
  }

  function initSection(section) {
    if (!section || !section.classList.contains(SECTION_CLASS)) return;

    const root = section.querySelector(".feature-details");
    if (!root) return;

    if (root._featureDetailsInstance) {
      root._featureDetailsInstance.destroy();
      root._featureDetailsInstance = null;
    }
    root._featureDetailsInstance = new FeatureDetails(root);
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeFeatureDetailsEvents) {
    window.__themeFeatureDetailsEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      destroySection(event.target);
    });
  }
})();
